import { useConfig } from 'config/use-config';
import { useRouter } from 'next/router';
import {
  FC,
  PropsWithChildren,
  useMemo,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useSessionStorage } from 'react-use';
import { retrieveValueFromUrlQuery } from 'utils/query-helper';
import { typedKeys } from 'utils/type-helper';
import { CATEGORIES, FUNNEL_STEPS, PET_TYPE } from './constants';
import { PetType } from 'constants/pet-type';

type FunnelPetType = PET_TYPE | null;
interface SubscriptionFunnelContextType {
  step: FUNNEL_STEPS;
  goToStep: (nextStep: FUNNEL_STEPS) => void;
  petType: FunnelPetType;
  setPetType: (petType: PET_TYPE) => void;
  subscriptionId: string;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  selectedCategories: CATEGORIES[];
  toggleFoodType: (category: CATEGORIES) => void;
  applicableCategories: Array<keyof typeof CATEGORIES>;
  /** True if subscription funnel should be act as 1 single purchase funnel */
  isSingleProductPurchase: boolean;
  /** True if we're dealing with existing subscription */
  isExistingSubscription: boolean;
}

const SubscriptionFunnelContext = createContext({} as SubscriptionFunnelContextType);

const SubscriptionFunnelProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    featureFlags: { availabilityBarf },
  } = useConfig();
  const router = useRouter();
  const { step } = router.query;

  const [loading, setLoading] = useState(true);

  const [petType, setPetType] = useSessionStorage<FunnelPetType>('futterabo:petType', null);
  const [selectedCategories, setSelectedCategories] = useSessionStorage<CATEGORIES[]>(
    'futterabo:category',
    []
  );

  const isExistingSubscription = !!router.query.existingsubs || !!router.query.id;

  const goToStep = useCallback(
    (nextStep: FUNNEL_STEPS) => {
      router.push({
        pathname: '/futterabo/[step]',
        query: {
          ...router.query,
          step: nextStep,
        },
      });
    },
    [router]
  );

  const toggleFoodType = useCallback(
    (category: CATEGORIES) => {
      const newFoodTypes = selectedCategories;

      if (selectedCategories.includes(category)) {
        newFoodTypes.splice(newFoodTypes.indexOf(category), 1);
      } else {
        newFoodTypes.push(category);
      }

      setSelectedCategories([...newFoodTypes]);
    },
    [selectedCategories, setSelectedCategories]
  );

  useEffect(() => {
    //  when the dog type is selected filter out cat related products
    if (petType === PetType.Dog && selectedCategories.includes(CATEGORIES.CAT_LITTER)) {
      setSelectedCategories(
        selectedCategories.filter((c) => {
          return c !== CATEGORIES.CAT_LITTER;
        })
      );
    }
    //  when the cat pet type is selected filter out dog related products
    if (
      petType === PetType.Cat &&
      (selectedCategories.includes(CATEGORIES.HEALTH) ||
        selectedCategories.includes(CATEGORIES.ACCESSORY))
    ) {
      setSelectedCategories(
        selectedCategories.filter((c) => {
          return c !== CATEGORIES.HEALTH && c !== CATEGORIES.ACCESSORY;
        })
      );
    }
  }, [petType, selectedCategories, setSelectedCategories]);

  const applicableCategories = useMemo(() => {
    return typedKeys(CATEGORIES).filter((category) => {
      if (!availabilityBarf && category === 'BARF') {
        return false;
      }

      if (!petType) {
        return category !== 'CAT_LITTER' && category !== 'HEALTH' && category !== 'ACCESSORY';
      }

      if (petType === PetType.Dog) {
        return category !== 'CAT_LITTER';
      }

      return category !== 'HEALTH' && category !== 'ACCESSORY';
    });
  }, [petType, availabilityBarf]);

  const subscriptionId = useMemo(
    () =>
      retrieveValueFromUrlQuery({
        query: router.query,
        paramKey: 'id',
      }),
    [router.query]
  );

  return (
    <SubscriptionFunnelContext.Provider
      value={{
        step: step as FUNNEL_STEPS,
        goToStep,
        petType,
        setPetType,
        selectedCategories,
        toggleFoodType,
        subscriptionId,
        loading,
        setLoading,
        applicableCategories,
        isSingleProductPurchase: !!router.query.variantId,
        isExistingSubscription,
      }}
    >
      {children}
    </SubscriptionFunnelContext.Provider>
  );
};

const useSubscriptionFunnel = (): SubscriptionFunnelContextType =>
  useContext(SubscriptionFunnelContext);

export { SubscriptionFunnelProvider, useSubscriptionFunnel };
